// Add row and column functionality for PdfTableViewer
import { USE_SERVER_SIDE_GRIDLINES } from "../config/gridLinesConfig";
import {
  extractCellBoundsFromTable,
  generateGridLinesFromCellBounds,
} from "../utils/gridCalculationUtils";
import {
  getCanvasCoordinates,
  cloneGridLines,
} from "../utils/pdfTableViewerUtils";

/**
 * Calculate dynamic grid lines based on server data or cell content
 */
export const calculateDynamicGridLines = (
  selectedTable,
  tableData,
  currentPage,
  pdfPageDimensions,
  limitToCurrentPageOnly = false
) => {
  if (!selectedTable || !tableData) {
    return null;
  }

  // PRIORITY 1: Use server-provided grid lines if available and flag is true
  if (USE_SERVER_SIDE_GRIDLINES && selectedTable.grid_lines) {
    if (limitToCurrentPageOnly) {
      // Only return the current page's grid lines
      return selectedTable.grid_lines[currentPage]
        ? {
            [currentPage]: selectedTable.grid_lines[currentPage],
          }
        : null;
    } else {
      // Return all available grid lines from server (already in page-indexed format)
      return selectedTable.grid_lines;
    }
  }

  // FALLBACK: Calculate grid lines from cell bounds if server data not available or flag is false

  // Extract cell bounds using shared utility
  const cellBounds = extractCellBoundsFromTable(
    selectedTable,
    currentPage,
    pdfPageDimensions
  );

  // Generate grid lines from cell bounds using shared utility
  const gridLinesForPage = generateGridLinesFromCellBounds(cellBounds);

  // Return in consistent format - always as page-indexed object
  const gridLines = {
    [currentPage]: gridLinesForPage,
  };

  return gridLines;
};

/**
 * Create add row/column functionality
 */
export const createAddRowColumnFunctions = (
  tableData,
  dynamicGridLines,
  setDynamicGridLines,
  currentPage,
  isPlacingGridLine,
  setIsPlacingGridLine,
  placingGridLineType,
  setPlacingGridLineType,
  setPlacingGridLinePosition,
  setDrawingMode,
  setIsDrawing,
  setGridLineDragMode,
  originalGridLines,
  setOriginalGridLines,
  canvasRef,
  highlightContainerRef,
  selectedTable,
  pdfPageDimensions
) => {
  /**
   * Start interactive row placement
   */
  const startAddNewRow = () => {
    if (!tableData) return;

    // Enter placement mode for rows
    setIsPlacingGridLine(true);
    setPlacingGridLineType("row");
    setPlacingGridLinePosition(null);

    // Clear other modes
    setDrawingMode(null);
    setIsDrawing(false);
    setGridLineDragMode(false);
  };

  /**
   * Start interactive column placement
   */
  const startAddNewColumn = () => {
    if (!tableData) {
      return;
    }

    // Enter placement mode for columns
    setIsPlacingGridLine(true);
    setPlacingGridLineType("column");
    setPlacingGridLinePosition(null);

    // Clear other modes
    setDrawingMode(null);
    setIsDrawing(false);
    setGridLineDragMode(false);
  };

  /**
   * Handle click on PDF to place new grid line
   */
  const handleGridLinePlacement = (event) => {
    if (!isPlacingGridLine) {
      return;
    }

    // Ensure grid lines exist, if not create basic structure
    if (!dynamicGridLines?.[currentPage]) {
      const newGridLines = calculateDynamicGridLines(
        selectedTable,
        tableData,
        currentPage,
        pdfPageDimensions,
        false // limitToCurrentPageOnly - always false for grid line placement
      );

      if (newGridLines) {
        setDynamicGridLines(newGridLines);

        // Schedule the placement for the next tick after grid lines are set
        setTimeout(() => handleGridLinePlacement(event), 10);
        return;
      } else {
        return;
      }
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    // Convert to PDF coordinates
    const pdfCoords = getCanvasCoordinates(
      event,
      canvasRef,
      highlightContainerRef
    );

    if (!pdfCoords) {
      return;
    }

    const currentGridLines = dynamicGridLines[currentPage];

    // This captures the state before any changes for proper before/after comparison
    const storedOriginalGridLines = cloneGridLines(dynamicGridLines);
    setOriginalGridLines(storedOriginalGridLines);

    const newGridLines = {
      ...dynamicGridLines,
      [currentPage]: {
        ...currentGridLines,
        horizontal: [...(currentGridLines.horizontal || [])],
        vertical: [...(currentGridLines.vertical || [])],
      },
    };

    // Add new grid line
    if (placingGridLineType === "row") {
      // Add horizontal line for new row
      let tableMinX, tableMaxX;

      if (
        currentGridLines.horizontal &&
        currentGridLines.horizontal.length > 0
      ) {
        tableMinX = Math.min(...currentGridLines.horizontal.map((h) => h.x0));
        tableMaxX = Math.max(...currentGridLines.horizontal.map((h) => h.x1));
      } else if (
        currentGridLines.vertical &&
        currentGridLines.vertical.length > 0
      ) {
        tableMinX = Math.min(...currentGridLines.vertical.map((v) => v.x0));
        tableMaxX = Math.max(...currentGridLines.vertical.map((v) => v.x1));
      } else {
        // Fallback to canvas bounds
        const pdfWidth = pdfPageDimensions.current.width;
        tableMinX = 0;
        tableMaxX = pdfWidth;
      }

      const newHorizontalLine = {
        x0: tableMinX,
        y0: pdfCoords.y,
        x1: tableMaxX,
        y1: pdfCoords.y,
      };

      newGridLines[currentPage].horizontal.push(newHorizontalLine);
    } else if (placingGridLineType === "column") {
      // Add vertical line for new column
      let tableMinY, tableMaxY;

      if (currentGridLines.vertical && currentGridLines.vertical.length > 0) {
        tableMinY = Math.min(...currentGridLines.vertical.map((v) => v.y0));
        tableMaxY = Math.max(...currentGridLines.vertical.map((v) => v.y1));
      } else if (
        currentGridLines.horizontal &&
        currentGridLines.horizontal.length > 0
      ) {
        tableMinY = Math.min(...currentGridLines.horizontal.map((h) => h.y0));
        tableMaxY = Math.max(...currentGridLines.horizontal.map((h) => h.y1));
      } else {
        // Fallback to canvas bounds
        const pdfHeight = pdfPageDimensions.current.height;
        tableMinY = 0;
        tableMaxY = pdfHeight;
      }

      const newVerticalLine = {
        x0: pdfCoords.x,
        y0: tableMinY,
        x1: pdfCoords.x,
        y1: tableMaxY,
      };

      newGridLines[currentPage].vertical.push(newVerticalLine);
    }

    setDynamicGridLines(newGridLines);

    // Reset placement mode
    setIsPlacingGridLine(false);
    setPlacingGridLineType(null);
    setPlacingGridLinePosition(null);
  };

  /**
   * Cancel grid line placement
   */
  const cancelGridLinePlacement = () => {
    // Reset placement states
    setIsPlacingGridLine(false);
    setPlacingGridLineType(null);
    setPlacingGridLinePosition(null);

    // Restore original grid lines if available
    if (originalGridLines) {
      setDynamicGridLines(originalGridLines);
      setOriginalGridLines(null);
    }
  };

  return {
    startAddNewRow,
    startAddNewColumn,
    handleGridLinePlacement,
    cancelGridLinePlacement,
    calculateDynamicGridLines: (limitToCurrentPageOnly = false) =>
      calculateDynamicGridLines(
        selectedTable,
        tableData,
        currentPage,
        pdfPageDimensions,
        limitToCurrentPageOnly
      ),
  };
};
